import { useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CatagoryTypes } from '../../../../constants.js';
import SearchableDropdown from '../../../../components/FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import OrganisationDropdown from '../../../../components/Shared/OrganisationDropdown/index.tsx';
import { useWizard } from '../../hooks/WizardProvider.jsx';
import StepPanel from '../../components/StepPanel.jsx';
import WizardFooter from '../../components/WizardFooter.jsx';

export default function OrganisationMapping() {
  const { draft, setDraft, steps, stepErrors, currentStep, organisationId } = useWizard();
  const mapping = draft.organisation_mapping;
  const createdOrgId = organisationId || draft.organisation.organisation_id;

  useEffect(() => {
    if (!createdOrgId) return;
    const orgName = draft.organisation.name;
    const orgCategory = draft.organisation.category;
    const categoryOption =
      CatagoryTypes.find((c) => c.value === orgCategory) ??
      (orgCategory ? { value: orgCategory, label: orgCategory } : null);

    setDraft((prev) => {
      if (prev.organisation_mapping.secondOrganisation?.organisation_id === createdOrgId) {
        return prev;
      }
      return {
        ...prev,
        organisation_mapping: {
          ...prev.organisation_mapping,
          secondCategory: categoryOption,
          secondOrganisation: {
            organisation_id: createdOrgId,
            name: orgName || createdOrgId,
            category: orgCategory,
          },
        },
      };
    });
  }, [createdOrgId, draft.organisation.name, draft.organisation.category, setDraft]);

  const updateMapping = (patch) => {
    setDraft((prev) => ({
      ...prev,
      organisation_mapping: { ...prev.organisation_mapping, ...patch },
    }));
  };

  if (!createdOrgId) {
    return (
      <>
        <StepPanel stepId={3} stepStatus={steps.organisation_mapping}>
          <Alert severity="warning">
            Complete <strong>Organisation Details</strong> (step 1) and <strong>Account Creation</strong> (step 2) first. The new organisation must
            exist before you can create mappings.
          </Alert>
        </StepPanel>
        <WizardFooter stepId={currentStep} />
      </>
    );
  }

  return (
    <>
      <StepPanel stepId={3} stepStatus={steps.organisation_mapping}>
        <Alert severity="info" sx={{ mb: 3 }}>
          New organisation created: <strong>{draft.organisation.name || createdOrgId}</strong> (
          {createdOrgId}). Link it with an existing organisation — same flow→
          Organisations Mapping.
        </Alert>

        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Create New Organisation Mapping
        </Typography>

        <Box sx={{ mb: 3 }}>
          <SearchableDropdown
            label="Select Source Category"
            options={CatagoryTypes}
            value={mapping.firstCategory}
            labelKey="label"
            valueKey="value"
            onChange={(selected) => {
              const item = Array.isArray(selected) ? selected[0] : selected;
              updateMapping({
                firstCategory: item,
                firstOrganisation: null,
              });
            }}
            error={!!stepErrors.firstCategory}
            errorMessage={stepErrors.firstCategory}
            size="small"
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <OrganisationDropdown
            label={
              <Typography component="span">
                Select Source Organisation <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            value={mapping.firstOrganisation}
            onChange={(org) => updateMapping({ firstOrganisation: org })}
            categoryType={mapping.firstCategory?.value || ''}
            error={!!stepErrors.firstOrganisation}
            errorMessage={stepErrors.firstOrganisation}
            autoSelectValue={false}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <SearchableDropdown
            label="Select Category You Want To Work With"
            options={CatagoryTypes}
            value={mapping.secondCategory}
            labelKey="label"
            valueKey="value"
            onChange={(selected) => {
              const item = Array.isArray(selected) ? selected[0] : selected;
              updateMapping({
                secondCategory: item,
              });
            }}
            size="small"
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <OrganisationDropdown
            label={
              <Typography component="span">
                Select Organization <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            value={mapping.secondOrganisation}
            onChange={(org) => updateMapping({ secondOrganisation: org })}
            categoryType={mapping.secondCategory?.value || draft.organisation.category || ''}
            error={!!stepErrors.secondOrganisation}
            errorMessage={stepErrors.secondOrganisation}
            autoSelectValue={false}
          />
        </Box>
      </StepPanel>
      <WizardFooter stepId={currentStep} />
    </>
  );
}
